/**
 * src/components/voice/Waveform.tsx
 * ------------------------------------
 * Animated audio waveform bars, ported from the Initiation prototype (orb.jsx).
 * Self-animates via requestAnimationFrame when `active`. If real mic
 * `amplitudes` are supplied they drive the bar heights instead.
 */
import { useEffect, useRef } from 'react'

interface WaveformProps {
  active?: boolean
  bars?: number
  color?: string
  /** Optional live amplitudes (0–1). When present + active, drive bar heights. */
  amplitudes?: number[]
  className?: string
  style?: React.CSSProperties
}

export function Waveform({
  active = false,
  bars = 28,
  color = 'var(--brand-violet)',
  amplitudes,
  className,
  style,
}: WaveformProps) {
  const ref = useRef<HTMLDivElement>(null)
  const ampRef = useRef<number[] | undefined>(amplitudes)
  ampRef.current = amplitudes

  useEffect(() => {
    let raf = 0
    const tick = () => {
      const t = performance.now() / 1000
      const el = ref.current
      const amps = ampRef.current
      if (el) {
        const children = el.children
        for (let i = 0; i < children.length; i++) {
          const b = children[i] as HTMLElement
          let h: number
          if (active && amps && amps.length > 0) {
            h = Math.max(0.06, amps[i % amps.length])
          } else {
            const phase = (i / bars) * Math.PI * 2
            h = active
              ? 0.4 + Math.abs(Math.sin(t * 4 + phase) * 0.5 + Math.cos(t * 7 + phase) * 0.3)
              : 0.18 + Math.sin(t * 1.5 + phase) * 0.08
          }
          b.style.height = `${Math.max(2, h * 100)}%`
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [active, bars])

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={className}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, height: 28, ...style }}
    >
      {Array.from({ length: bars }).map((_, i) => (
        <span
          key={i}
          style={{ width: 2.5, background: color, borderRadius: 2, transition: 'height .08s linear' }}
        />
      ))}
    </div>
  )
}
